import i18n from 'i18n';
import {
  CreateRecyclingConsignmentOrderV2SchemaType,
  FORM_KEY,
  recycleConsignmentStoreList,
} from './common';
import LOCALS from 'commons/locals';
import { message, Modal, Button } from 'antd';
import { MutableRefObject, useCallback, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { findLabelByValue, OMS_RECYCLE_ORDER_TYPE_MAP } from 'commons/options';
import {
  generateNote,
  generateSubTitle,
} from 'pages/product/create-page-v2/common';
import { useAppSelector } from 'store/hooks';
import {
  GlobalInfoState,
  selectGlobalInfo,
  SelectOptionLabelList,
} from 'store/slices/globalInfoSlice';
import useBrandList, { BrandType } from 'commons/hooks/use-brand-list';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import { fetchRecycleOrderCreate, fetchRecycleOrderDetail } from 'apis/oms';
import { useNavigate } from 'react-router-dom';
import { openSignedRecycleOrderContract } from 'utils/openSignedRecycleOrderContract';
import type { AiIdentifyPicsRef } from './product-list/form-items/ai-identify-pics';
import useResource from 'commons/hooks/useResource';
import { validateBkcAiAuthentication } from './bkc-ai-authentication-validation';
import { updateAuthenticationReviewStatus } from './product-list/update-authentication-review-status';
import { expandProductListForSubmit } from './twilly';
import { getShopConfig, SHOP_MAP } from 'constant/shop';
import authenticationApi from '../../../apis/authentication';
import xmAssetsApi from 'apis/xm-assets';
import { resolveProductTaxRegime } from 'constants/france-tax';

// TODO: 逻辑有点重复了，需要统一到 create-page-v2 的 common.ts 中
const generateDetailTitle = ({
  productCategoryIdList,
  brandName,
  rank,
  name,
  color,
  material,
  hardware,
  stamp,
  rankSelectOptions,
  brandOptionList,
  colorSelectOptions,
  hardwareSelectOptions,
  stampSelectOptions,
  materialCascaderOptionsMap,
}: {
  productCategoryIdList: CreateRecyclingConsignmentOrderV2SchemaType['productList'][number]['productCategoryIdList'];
  brandName: CreateRecyclingConsignmentOrderV2SchemaType['productList'][number]['brandName'];
  rank: CreateRecyclingConsignmentOrderV2SchemaType['productList'][number]['rank'];
  name: CreateRecyclingConsignmentOrderV2SchemaType['productList'][number]['name'];
  color: CreateRecyclingConsignmentOrderV2SchemaType['productList'][number]['color'];
  material: CreateRecyclingConsignmentOrderV2SchemaType['productList'][number]['material'];
  hardware: CreateRecyclingConsignmentOrderV2SchemaType['productList'][number]['hardware'];
  stamp: CreateRecyclingConsignmentOrderV2SchemaType['productList'][number]['stamp'];
  rankSelectOptions: SelectOptionLabelList;
  brandOptionList: BrandType[];
  colorSelectOptions: GlobalInfoState['colorSelectOptions'];
  materialCascaderOptionsMap: GlobalInfoState['materialCascaderOptionsMap'];
  hardwareSelectOptions: GlobalInfoState['hardwareSelectOptions'];
  stampSelectOptions: GlobalInfoState['stampSelectOptions'];
}) => {
  const detailTitleEnArr: string[] = [];
  const detailTitleJaArr: string[] = [];
  const detailTitleZhCnArr: string[] = [];
  const detailTitleZhTwArr: string[] = [];

  const rankOption = rankSelectOptions.find((d) => d.value === rank);
  if (rankOption) {
    detailTitleEnArr.push(rankOption.labelEn || '');
    detailTitleJaArr.push(rankOption.labelJa || '');
    detailTitleZhCnArr.push(rankOption.labelCn || '');
    detailTitleZhTwArr.push(rankOption.labelTw || '');

    const brandOption = brandOptionList.find((d) => d.value === brandName);
    if (brandOption) {
      detailTitleEnArr.push(brandOption.descEn || '');
      detailTitleJaArr.push(brandOption.descJa || '');
      detailTitleZhCnArr.push(brandOption.descZh || '');
      detailTitleZhTwArr.push(brandOption.descZhTw || '');
    }
  }

  if (name) {
    detailTitleEnArr.push(name.en || '');
    detailTitleJaArr.push(name.ja || '');
    detailTitleZhCnArr.push(name.zh_CN || '');
    detailTitleZhTwArr.push(name.zh_TW || '');
  }

  if (color && color.length) {
    color.forEach((color) => {
      const colorOption = colorSelectOptions.find((d) => d.value === color);
      if (colorOption) {
        detailTitleEnArr.push(colorOption.labelEn || '');
        detailTitleJaArr.push(colorOption.labelJa || '');
        detailTitleZhCnArr.push(colorOption.labelCn || '');
        detailTitleZhTwArr.push(colorOption.labelTw || '');
      }
    });
  }

  if (material && material.length) {
    const firstLevelId = productCategoryIdList[0];
    const materialCascaderOptions = materialCascaderOptionsMap[firstLevelId];
    material.forEach((material) => {
      const materialOption = findCascaderOptionById(
        material[material.length - 1],
        materialCascaderOptions,
      );
      if (materialOption) {
        detailTitleEnArr.push(materialOption.labelEn || '');
        detailTitleJaArr.push(materialOption.labelJa || '');
        detailTitleZhCnArr.push(materialOption.labelCn || '');
        detailTitleZhTwArr.push(materialOption.labelTw || '');
      }
    });
  }

  if (hardware && hardware.length) {
    hardware.forEach((hardware) => {
      const hardwareOption = hardwareSelectOptions.find(
        (d) => d.value === hardware && d.value !== 'none',
      );
      if (hardwareOption) {
        detailTitleEnArr.push(hardwareOption.labelEn || '');
        detailTitleJaArr.push(hardwareOption.labelJa || '');
        detailTitleZhCnArr.push(hardwareOption.labelCn || '');
        detailTitleZhTwArr.push(hardwareOption.labelTw || '');
      }
    });
  }

  if (stamp) {
    const stampOption = stampSelectOptions.find(
      (d) => d.value === stamp && d.value !== 'none',
    );
    if (stampOption) {
      detailTitleEnArr.push(stampOption.labelEn || '');
      detailTitleJaArr.push(stampOption.labelJa || '');
      detailTitleZhCnArr.push(stampOption.labelCn || '');
      detailTitleZhTwArr.push(stampOption.labelTw || '');
    }
  }

  return {
    en: detailTitleEnArr
      .map((d) => d.trim())
      .filter(Boolean)
      .join(' '),
    ja: detailTitleJaArr
      .map((d) => d.trim())
      .filter(Boolean)
      .join(' '),
    zh_CN: detailTitleZhCnArr
      .map((d) => d.trim())
      .filter(Boolean)
      .join(' '),
    zh_TW: detailTitleZhTwArr
      .map((d) => d.trim())
      .filter(Boolean)
      .join(' '),
  };
};

const SubmitConfirm = ({
  data,
  onCancel,
  totalCostPrice,
  totalProductCount,
  aiIdentifyRefs,
}: {
  data: CreateRecyclingConsignmentOrderV2SchemaType | null;
  onCancel: () => void;
  totalCostPrice: number;
  totalProductCount: number;
  aiIdentifyRefs: MutableRefObject<Record<string, AiIdentifyPicsRef | null>>;
}) => {
  const navigate = useNavigate();
  const {
    hardwareSelectOptions,
    materialCascaderOptionsMap,
    colorSelectOptions,
    stampSelectOptions,
    rankSelectOptions,
    productCategoryCascaderOptions,
    staffSelectOptions,
  } = useAppSelector(selectGlobalInfo);
  const canSkipBkcAiAuthentication = useResource(
    'recycling-consignment-bkc-ai-skip',
  );
  const { BrandList: brandOptionList } = useBrandList();
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [signedContractModalOpen, setSignedContractModalOpen] = useState(false);
  const [signedContractSubmitting, setSignedContractSubmitting] =
    useState(false);
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const [successData, setSuccessData] = useState<
    | {
        orderSn: string;
        id: string;
      }[]
    | null
  >(null);
  const [createdSubmissionId, setCreatedSubmissionId] = useState<number>();

  const receptionistName =
    data?.receptionistId !== undefined
      ? staffSelectOptions.find(
          (option) => option.value === data.receptionistId,
        )?.label
      : '';

  const clearAllAiIdentifyLocalPreviews = useCallback(() => {
    Object.values(aiIdentifyRefs.current).forEach((aiIdentifyRef) => {
      aiIdentifyRef?.clearLocalPreviews();
    });
  }, [aiIdentifyRefs]);

  const validateBkcAuthenticationBeforeCreate = useCallback(async () => {
    if (!data?.productList?.length) return true;

    let fetchErrorHandled = false;
    const result = await validateBkcAiAuthentication({
      productList: data.productList,
      canSkip: canSkipBkcAiAuthentication,
      productCategoryCascaderOptions,
      fetchAuthenticationDetail: async (authenticationBagId) => {
        try {
          return await authenticationApi.getAuthenticationDetail(
            String(authenticationBagId),
          );
        } catch (error: any) {
          fetchErrorHandled = !!error?._aiServiceHandled;
          throw error;
        }
      },
    });

    if (result.valid) return true;

    const index = result.productIndex + 1;
    if (result.reason === 'missing') {
      message.error(String(i18n.t(LOCALS.bkc_ai_identify_required, { index })));
    } else if (result.reason === 'no-task') {
      message.error(
        String(i18n.t(LOCALS.bkc_ai_identify_not_completed, { index })),
      );
    } else if (!fetchErrorHandled) {
      message.error(String(i18n.t(LOCALS.ai_identify_result_fetch_failed)));
    }
    return false;
  }, [canSkipBkcAiAuthentication, data, productCategoryCascaderOptions]);

  const onOk = useCallback(async () => {
    if (!data) return;
    setIsSubmiting(true);
    setCreatedSubmissionId(undefined);

    const bkcAuthenticationValid =
      await validateBkcAuthenticationBeforeCreate();
    if (!bkcAuthenticationValid) {
      setIsSubmiting(false);
      return;
    }

    const dto = {
      payoutDetail: data.payoutDetail,
      city: data.city,
      country: data.country,
      createdFrom: data.storeId,
      detailAddress: data.detailAddress,
      email: data.email,
      memberId: data.memberId,
      name: data.name,
      phone: [data.postCountryCode, data.postPhone].filter(Boolean).join(' '),
      phone2: [data.postCountryCode2, data.postPhone2]
        .filter(Boolean)
        .join(' '),
      postCode: data.postCode,
      postPhone: [data.postCountryCode, data.postPhone]
        .filter(Boolean)
        .join(' '),
      settlementType: data.settlementType,
      memberPaymentAccountType: data.memberPaymentAccountType,
      memberPaymentAccountSubType: data.memberPaymentAccountSubType,
      memberPaymentBankName: data.memberPaymentBankName,
      memberPaymentBankAddress: data.memberPaymentBankAddress,
      memberPaymentAccountNo: data.memberPaymentAccountNo,
      memberPaymentAccountName: data.memberPaymentAccountName,
      memberPaymentKiGo: data.memberPaymentKiGo,
      memberPaymentBangGo: data.memberPaymentBangGo,
      memberPaymentBankCountry: data.memberPaymentBankCountry,
      memberPaymentSwiftCode: data.memberPaymentSwiftCode,
      memberPaymentAccountAddress: data.memberPaymentAccountAddress,
      memberPaymentBankNo: data.memberPaymentBankNo,
      memberPaymentFpsNo: data.memberPaymentFpsNo,
      memberPaymentFpsAccountName: data.memberPaymentFpsAccountName,
      memberPaymentBankPhone: data.memberPaymentBankPhone,
      memberPaymentPayNowNo: data.memberPaymentPayNowNo,
      memberPaymentPayNowAccountName: data.memberPaymentPayNowAccountName,
      staffName: data.staffName,
      receptionistId: data.receptionistId,
      storeId: data.storeId,
      type: data.type,
      isTradeIn: data.isTradeIn,
      supplierType:
        data.storeId === SHOP_MAP.FRANCE
          ? data.supplierType || undefined
          : undefined,
      productParamList: expandProductListForSubmit(
        data.productList,
        productCategoryCascaderOptions,
      ).map((item) => {
        const firstLevelId = item.productCategoryIdList[0];
        const materialCascaderOptions =
          materialCascaderOptionsMap[firstLevelId];
        const noteI18n = generateNote(
          item.material,
          item.hardware,
          materialCascaderOptions,
          hardwareSelectOptions,
        );
        const subTitleI18n = generateSubTitle(
          item.name,
          item.color,
          colorSelectOptions,
        );
        const detailTitleI18n = generateDetailTitle({
          productCategoryIdList: item.productCategoryIdList,
          brandName: item.brandName,
          rank: item.rank,
          name: item.name,
          color: item.color,
          material: item.material,
          hardware: item.hardware,
          stamp: item.stamp,
          rankSelectOptions,
          brandOptionList,
          colorSelectOptions,
          materialCascaderOptionsMap,
          hardwareSelectOptions,
          stampSelectOptions,
        });

        const facebookEnabled = item.material.some((path) => {
          if (path.includes('exotic-skin')) return true;
          // Leaf-only paths (from auth task data) lack the parent category —
          // fall back to treeIds lookup to find the root category.
          const leaf = path[path.length - 1];
          const option = findCascaderOptionById(
            leaf,
            materialCascaderOptions || [],
          );
          return option?.treeIds?.split(',').includes('exotic-skin') ?? false;
        })
          ? 0
          : 1;

        const taxRegime =
          data.storeId === SHOP_MAP.FRANCE
            ? data.taxRegime ||
              resolveProductTaxRegime(data.type, data.supplierType)
            : undefined;

        return {
          accessory: item.accessory.join(','),
          attrRankDesc: item.rankDesc.en,
          attrSize: item.attrSize.en,
          brandName: item.brandName,
          color: item.color.join(','),
          colors: item.colors,
          attrColorRemark: item.colorRemark || undefined,
          attrMaterialRemark: item.materialRemark || undefined,
          attrHardwareRemark: item.hardwareRemark || undefined,
          attrStampRemark: item.stampRemark || undefined,
          detailTitle: detailTitleI18n.en,
          facebookEnabled,
          finalRecyclePrice:
            data.type === OMS_RECYCLE_ORDER_TYPE_MAP.RECYCLE
              ? (item.finalRecyclePrice ?? undefined)
              : undefined,
          finalSalePrice:
            data.type === OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT
              ? (item.finalSalePrice ?? undefined)
              : undefined,
          firstValuationShopRemark:
            item.firstValuationShopRemark || data.baseShopRemark || '',
          firstValuationStock: item.firstValuationStock,
          firstValuationStockRemark: item.firstValuationStockRemark,
          fullStamp: item.fullStamp,
          guestRemarks: item.guestRemarks,
          hardware: item.hardware.join(','),
          i18nCn: {
            attrRankDesc: item.rankDesc.zh_CN,
            attrSize: item.attrSize.zh_CN,
            name: item.name.zh_CN,
            note: noteI18n.zh_CN,
            subTitle: subTitleI18n.zh_CN,
            detailTitle: detailTitleI18n.zh_CN,
          },
          i18nJa: {
            attrRankDesc: item.rankDesc.ja,
            attrSize: item.attrSize.ja,
            name: item.name.ja,
            note: noteI18n.ja,
            subTitle: subTitleI18n.ja,
            detailTitle: detailTitleI18n.ja,
          },
          i18nTw: {
            attrRankDesc: item.rankDesc.zh_TW,
            attrSize: item.attrSize.zh_TW,
            name: item.name.zh_TW,
            note: noteI18n.zh_TW,
            subTitle: subTitleI18n.zh_TW,
            detailTitle: detailTitleI18n.zh_TW,
          },
          material: item.material
            .map((i) => {
              return i[i.length - 1];
            })
            .join(','),
          name: item.name.en,
          note: noteI18n.en,
          productCategoryId:
            item.productCategoryIdList[item.productCategoryIdList.length - 1],
          productPics: item.productPics,
          productTitle: '', // 没啥用这玩意
          rank: item.rank,
          stamp: item.stamp,
          storeReceiptPics:
            item.storeReceiptPics.length > 0
              ? JSON.stringify(item.storeReceiptPics)
              : '',
          subTitle: subTitleI18n.en,
          authenticationBagId: item.authenticationBagId,
          isFakeBag: item.isFakeBag,
          fakeBagReason: item.fakeBagReason,
          reviewerName: item.reviewerName,
          initXmAssets: item.initXmAssets ?? false,
          price: item.price ?? undefined,
          taxRegime,
        };
      }),
    };

    try {
      const result = await fetchRecycleOrderCreate(dto);
      const dParse = JSON.parse(result.data) as {
        orderSn: string;
        id: string;
      }[];

      const expandedProductList = expandProductListForSubmit(
        data.productList,
        productCategoryCascaderOptions,
      );

      // 同一个订单 id 在本次提交里只会真正发出一次 /oms/recycleOrder/info 请求，
      // 后续 orderId 重复的调用方直接复用同一个 in-flight/已完成的 Promise。
      const orderDetailPromiseCache = new Map<
        string,
        ReturnType<typeof fetchRecycleOrderDetail>
      >();
      const getOrderDetail = (id: string) => {
        let cached = orderDetailPromiseCache.get(id);
        if (!cached) {
          cached = fetchRecycleOrderDetail({ id });
          orderDetailPromiseCache.set(id, cached);
        }
        return cached;
      };

      void Promise.all(
        data.productList.map((item) => {
          if (!item.authenticationBagId) return Promise.resolve();
          return updateAuthenticationReviewStatus(
            item.authenticationBagId,
            !!item.isFakeBag,
          ).catch(() => undefined);
        }),
      ).then(async () => {
        void Promise.all(
          expandedProductList.map((item, index) => {
            if (!item.authenticationBagId) return Promise.resolve();
            const orderId = dParse[index]?.id;
            if (!orderId) return Promise.resolve();
            return authenticationApi
              .syncImagesToDatalake({ omsRecycleOrderId: orderId })
              .catch(() => undefined);
          }),
        );

        const productIdsToInitXmAssets = (
          await Promise.all(
            expandedProductList.map(async (item, index) => {
              if (!item.initXmAssets) return undefined;
              const orderId = dParse[index]?.id;
              if (!orderId) return undefined;
              const { data: orderDetail } = await getOrderDetail(
                orderId,
              ).catch(() => ({ data: undefined }));
              return orderDetail?.omsRecycleOrderProduct?.id;
            }),
          )
        ).filter((id): id is number => typeof id === 'number');

        if (productIdsToInitXmAssets.length === 0) return;

        await xmAssetsApi
          .initializeAssets({ productIds: productIdsToInitXmAssets })
          .catch(() => undefined);
      });

      window.localStorage.removeItem(FORM_KEY);
      setSuccessData(dParse);
      if (dParse[0]?.id) {
        const { data: firstOrder } = await getOrderDetail(dParse[0].id);
        setCreatedSubmissionId(firstOrder.omsRecycleOrder?.submissionId);
      }
      clearAllAiIdentifyLocalPreviews();
    } finally {
      setIsSubmiting(false);
    }
  }, [
    brandOptionList,
    clearAllAiIdentifyLocalPreviews,
    colorSelectOptions,
    data,
    hardwareSelectOptions,
    materialCascaderOptionsMap,
    productCategoryCascaderOptions,
    rankSelectOptions,
    stampSelectOptions,
    validateBkcAuthenticationBeforeCreate,
  ]);

  const printSuccessOrders = useCallback(
    (signatureImage?: string) => {
      if (!successData?.length) return;

      const orderIdList = successData.map((item) => `${item.id}`);

      if (signatureImage) {
        openSignedRecycleOrderContract({
          orderIdList,
          signatureImage,
        });
        return;
      }

      window.open(
        `/prints/oms-recycle-order-contract?orderIdList=${orderIdList.join(',')}`,
      );
    },
    [successData],
  );

  const handlePrintReceipt = useCallback(async () => {
    setIsPrinting(true);
    try {
      printSuccessOrders();
    } finally {
      setIsPrinting(false);
    }
  }, [printSuccessOrders]);

  const handleSignedContractSubmit = useCallback(async () => {
    const signature = signatureRef.current;
    if (!signature || signature.isEmpty()) {
      message.warning(
        String(i18n.t(LOCALS.signed_contract_signature_required)),
      );
      return;
    }

    setSignedContractSubmitting(true);
    try {
      printSuccessOrders(signature.getTrimmedCanvas().toDataURL('image/png'));
      setSignedContractModalOpen(false);
      signatureRef.current?.clear();
    } catch (error) {
      console.error('Error generating signed contract PDF:', error);
      message.error(String(i18n.t(LOCALS.signed_contract_generate_pdf_failed)));
    } finally {
      setSignedContractSubmitting(false);
    }
  }, [printSuccessOrders]);

  const navigateToListAfterCreate = useCallback(() => {
    if (createdSubmissionId) {
      navigate(
        `/rrs/recycling-consignment-list?submissionId=${createdSubmissionId}&status=all`,
      );
      return;
    }

    navigate('/rrs/recycling-consignment-list');
  }, [createdSubmissionId, navigate]);

  return (
    <>
      <Modal
        open={!!data}
        onCancel={
          successData
            ? () => {
                navigateToListAfterCreate();
              }
            : onCancel
        }
        title={
          successData
            ? i18n.t(LOCALS.successful_operation)
            : i18n.t(LOCALS.confirm_submit)
        }
        onOk={successData ? undefined : onOk}
        okText={successData ? undefined : i18n.t(LOCALS.confirm)}
        confirmLoading={isSubmiting || isPrinting}
        footer={
          successData
            ? [
                <Button key="cancel" onClick={navigateToListAfterCreate}>
                  {i18n.t(LOCALS.cancel)}
                </Button>,
                <Button
                  key="signed-pdf"
                  onClick={() => {
                    signatureRef.current?.clear();
                    setSignedContractModalOpen(true);
                  }}
                  disabled={isPrinting || signedContractSubmitting}
                >
                  {i18n.t(LOCALS.signed_contract_generate_pdf)}
                </Button>,
                <Button
                  key="print"
                  type="primary"
                  loading={isPrinting}
                  disabled={signedContractSubmitting}
                  onClick={handlePrintReceipt}
                >
                  {i18n.t(LOCALS.yYqvdvcMFh)}
                </Button>,
              ]
            : undefined
        }
      >
        {data && !successData ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-32 text-right text-gray-500">
                {i18n.t(LOCALS.contract_type)}:
              </div>
              <div className="text-red-500 font-bold text-lg">
                {data.type === OMS_RECYCLE_ORDER_TYPE_MAP.RECYCLE
                  ? i18n.t(LOCALS.sell)
                  : i18n.t(LOCALS.consignment)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-32 text-right text-gray-500">
                {i18n.t(LOCALS.shop)}:
              </div>
              <div>
                {findLabelByValue(data.storeId, recycleConsignmentStoreList)}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-32 text-right text-gray-500">
                {i18n.t(LOCALS.staff)}:
              </div>
              <div>{data.staffName}</div>
            </div>
            {data.receptionistId !== undefined && (
              <div className="flex items-center gap-2">
                <div className="w-32 text-right text-gray-500">
                  {i18n.t(LOCALS.reception_staff)}:
                </div>
                <div>{receptionistName}</div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <div className="w-32 text-right text-gray-500">
                {i18n.t(LOCALS.DSLcOTUUkJ)}:
              </div>
              <div>{totalProductCount}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-32 text-right text-gray-500">
                {i18n.t(LOCALS.consignment_total_cost_price)}:
              </div>
              <div>
                {getShopConfig(data.storeId).currency}{' '}
                {totalCostPrice.toLocaleString('en-US')}
              </div>
            </div>
          </div>
        ) : null}

        {successData && (
          <div>
            {successData.map((d) => (
              <div key={d.id}>
                <a
                  href={`/rrs/recycling-consignment-detail/${d.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {d.orderSn}
                </a>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={signedContractModalOpen}
        title={i18n.t(LOCALS.signed_contract_generate_pdf)}
        width={960}
        onCancel={() => {
          if (signedContractSubmitting) return;
          setSignedContractModalOpen(false);
          signatureRef.current?.clear();
        }}
        footer={
          <>
            <Button
              onClick={() => signatureRef.current?.clear()}
              disabled={signedContractSubmitting}
            >
              {i18n.t(LOCALS.signed_contract_clear_signature)}
            </Button>
            <Button
              onClick={() => {
                if (signedContractSubmitting) return;
                setSignedContractModalOpen(false);
                signatureRef.current?.clear();
              }}
              disabled={signedContractSubmitting}
            >
              {i18n.t(LOCALS.cancel)}
            </Button>
            <Button
              type="primary"
              loading={signedContractSubmitting}
              onClick={handleSignedContractSubmit}
            >
              {i18n.t(LOCALS.signed_contract_preview_and_send)}
            </Button>
          </>
        }
      >
        <div className="mb-4 text-gray-600">
          {i18n.t(LOCALS.signed_contract_merge_hint_created, {
            count: successData?.length || 0,
          })}
        </div>
        <div className="mb-2 font-medium">
          {i18n.t(LOCALS.signed_contract_customer_signature)}
        </div>
        <div className="border border-gray-300 rounded bg-white overflow-hidden">
          <SignatureCanvas
            ref={signatureRef}
            penColor="black"
            backgroundColor="white"
            canvasProps={{
              width: 900,
              height: 260,
              className: 'block w-full h-[260px]',
            }}
          />
        </div>
        <div className="mt-2 text-gray-500">
          {i18n.t(LOCALS.signed_contract_signature_hint)}
        </div>
      </Modal>
    </>
  );
};

export default SubmitConfirm;
